import { useCallback, useState } from 'react';
import { useSelector, useIntl } from 'umi';
import { get, isEmpty } from 'lodash-es';
import { useCopilotOpenBridge } from '@/pages/aigc/pages/Agent/Copilot/hooks/useCopilotOpenBridge';
import { useDownloadNotification } from '@/pages/aigc/pages/Agent/ChatPage/components/DownloadLink/hooks';
import { useRequestController } from '@/pages/aigc/pages/Agent/Home/hooks/useRequestController';
import { downloadFile } from '@home/utils/file';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { getAuthorization } from '@/utils/utils';
import { flattenAdminGames } from '../HtmlPublishModal/utils';
import {
  buildAIGalleryReportEditConfig,
  REPORT_RULE_KEY_REGEX,
} from './buildAIGalleryReportEditConfig';

/** useRequestController 用引用相等判断 refreshDeps，不能写在 render 里 */
const USER_MESSAGE_BODY = { style_version: 'v2' };

/** AI-Gallery 详情接口返回的作品信息（仅取所需字段） */
interface IAiGalleryDashboardDetail {
  /** 当前用户是否为报告创建者或共同编辑协作者（即编辑权限） */
  is_mine?: boolean;
}

/**
 * AI Gallery Report 顶部工具条业务逻辑 Hook（Layer 2）
 *
 * 聚合「AI编辑」「下载」「发布」行为，并负责：
 * - 工具条可见性：详情 + 经分权限 + 用户信息均就绪后才展示（三按钮一起出现）
 * - 编辑按钮：is_mine + AI 权限
 * - 下载：auth-server download-package + 下载提示
 * - 发布：经分管理员游戏列表非空
 * - 编辑 / 下载点击埋点（70090116 / 70090117）
 */
export const useReportToolbarActions = (options: {
  /** 报告相对路径（/as/report/...） */
  path: string;
  /** 报告标题（用于下载文件名与下载提示） */
  title: string;
  /** 报告页面名（URL query name，作为 Copilot 引用来源的文件标签） */
  pageName: string;
}) => {
  const { path, title, pageName } = options;
  const { formatMessage } = useIntl();
  const { openCopilotWithDefaultAgent, isHaveAIPermission } =
    useCopilotOpenBridge();
  const { setNoticeStatus } = useDownloadNotification(title);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const { userInfo } = useSelector((state: any) => ({
    userInfo: state.home.userInfo,
  }));
  const isUserInfoReady = !!userInfo;

  // 从报告路径提取 rule_key，用于查询编辑权限 / 下载源码包
  const ruleKey = path.match(REPORT_RULE_KEY_REGEX)?.[1] ?? '';

  // 查询 AI-Gallery 详情接口，获取当前用户对该报告的编辑权限（is_mine）
  // 有 ruleKey 时才自动执行（manual 控制），无 ruleKey 时挂载不发起请求
  // errorHandler: 业务失败（如 40301）由本 hook 消化，不弹全局错误 toast
  const {
    data: dashboardDetail,
    loading: isDetailLoading,
    error: detailError,
  } = useRequestController({
    url: `/api/ai-gallery/dashboards/${ruleKey}`,
    method: 'GET',
    manual: !ruleKey,
    httpCfg: { errorHandler: true },
  });

  const isMine =
    !!(dashboardDetail as IAiGalleryDashboardDetail | undefined)?.is_mine;

  // 与发布弹窗游戏下拉同一接口 / 同一 cache：有经分游戏或系统管理员才显示【发布】
  const { data: gameData, loading: isGameAdminLoading } = useRequestController({
    method: 'POST',
    url: '/api/v1/dashboard/userMessage',
    refreshDeps: USER_MESSAGE_BODY,
    cacheKey: 'new-chart-game-list',
    cacheTime: 1800000,
    staleTime: 1800000,
  });

  // 详情 + 经分权限 + 用户信息都就绪后再展示，避免 Edit / Download / Publish 先后弹出
  const canShowToolbar =
    !isDetailLoading &&
    !isGameAdminLoading &&
    isUserInfoReady &&
    !detailError &&
    !!ruleKey &&
    dashboardDetail != null;

  const showEdit = isMine && isHaveAIPermission;
  const showPublish = !isEmpty(
    flattenAdminGames(get(gameData, 'main_game')),
  );

  /**
   * 拉起 Copilot，将该 HTML 报告链接填入会话并默认引用
   */
  const handleEdit = useCallback(() => {
    // 从 i18n 获取当前语言的默认提示语（中/英二选一）
    const config = buildAIGalleryReportEditConfig({
      path,
      pageName,
      title,
      query: formatMessage({ id: 'dbh_studio_aigallery_report_edit_query' }),
    });
    if (!config) return;
    openCopilotWithDefaultAgent(config);
    // 埋点（ID 内联，不使用常量），放方法最后执行
    buriedBtnPoint({
      buttonId: '70090116',
      buttonName: 'ai_edit',
      pageId: '701001',
      typeId: 'aigc',
      extInfo: JSON.stringify({ path, pageName, ruleKey }),
    });
  }, [path, title, pageName, ruleKey, formatMessage, openCopilotWithDefaultAgent]);

  /** 下载完整源码包：GET /api/auth-server/download-package?key= */
  const handleDownload = useCallback(async () => {
    if (!ruleKey || isDownloading) return;
    setIsDownloading(true);
    setNoticeStatus('loading');
    try {
      const res = await fetch(
        `/api/auth-server/download-package?key=${encodeURIComponent(ruleKey)}`,
        { headers: { Authorization: getAuthorization() || '' } },
      );
      // 失败时返回 JSON（HTTP 常为 200），成功为 html/zip 二进制
      if ((res.headers.get('Content-Type') || '').includes('application/json')) {
        throw new Error('download failed');
      }
      downloadFile(await res.blob(), title || ruleKey);
      setNoticeStatus('success');
    } catch {
      setNoticeStatus('error');
    } finally {
      setIsDownloading(false);
    }
    // 埋点（ID 内联，不使用常量），放方法最后执行
    buriedBtnPoint({
      buttonId: '70090117',
      buttonName: 'download',
      pageId: '701001',
      typeId: 'aigc',
      extInfo: JSON.stringify({ path, pageName, ruleKey }),
    });
  }, [path, pageName, ruleKey, title, isDownloading, setNoticeStatus]);

  return {
    /** 详情接口成功后才展示整条工具条 */
    canShowToolbar,
    showEdit,
    showPublish,
    isDownloading,
    handleEdit,
    handleDownload,
  };
};
